import React, { FC, useMemo, useState } from "react";
import styled from "styled-components";

import DataTable from "../components/DataTable";
import Components from "../components";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Button, IconButton } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import dayjs from "dayjs";
import { Helmet } from "react-helmet-async";
import PageTitle from "../components/PageTitle";
import apiClient from "../utils/apiClient";
import { GridRenderCellParams } from "@mui/x-data-grid/models/params/gridCellParams";
import type { GridColDef } from "@mui/x-data-grid/models/colDef/gridColDef";
import { useTranslation } from "react-i18next";

const { Popup, CreateLinkForm } = Components;

const HeadTabMenu = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  gap: 10px;

  & > div:not(:last-child) {
    margin-inline-end: 10px;
  }

  & > div:not(:first-child) {
    margin-inline-start: 10px;
  }
`;

const ButtonsBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  border: 0;
  position: relative;
  overflow: visible;
`;

type CopyButtonProps = {
  link: string;
};

const CopyButton: FC<CopyButtonProps> = ({ link }) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const clipboardAvailable = useMemo(
    () =>
      navigator.clipboard &&
      typeof navigator.clipboard.writeText === "function",
    [],
  );

  const handleCopy = async () => {
    if (!clipboardAvailable) {
      console.error("Clipboard API недоступен");
      return;
    }

    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error("Ошибка копирования:", err);
    }
  };

  if (!clipboardAvailable) {
    return null;
  }

  return (
    <>
      <IconButton
        aria-label="copy link"
        onClick={handleCopy}
        title={t("links.copyLink")}
      >
        <ContentCopyIcon />
      </IconButton>

      {copied && (
        <div
          style={{
            position: "absolute",
            top: "2px",
            insetInlineStart: "0px",
            background: "rgba(0, 0, 0, 0.8)",
            color: "white",
            padding: "2px 6px",
            height: "40px",
            borderRadius: "4px",
            fontSize: "12px",
            lineHeight: "16px",
            whiteSpace: "nowrap",
            zIndex: 10,
          }}
        >
          {t("links.linkCopied")}
        </div>
      )}
    </>
  );
};

const Links: FC = () => {
  const { t } = useTranslation();

  const [showPopup, setShowPopup] = useState(false);
  const [refreshCount, setRefreshCount] = useState(0);

  const handleCreateLink = () => {
    setShowPopup(true);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  const handleUpdate = () => {
    setRefreshCount((prev) => prev + 1);
  };

  const handleSuccessCreate = (newLink: string) => {
    console.log("Новая ссылка:", newLink);
    setRefreshCount((prev) => prev + 1);
  };

  const handleDeleteLink = async (item: any) => {
    const { id, name_link } = item;

    if (window.confirm(t("links.confirmDelete", { name: name_link }))) {
      try {
        await apiClient.delete(`/links/${id}`);
        handleUpdate();
        alert(t("links.deleted"));
      } catch (err) {
        console.error("Ошибка удаления ссылки:", err);
        alert(t("links.deleteError"));
      }
    }
  };

  const columns = React.useMemo<GridColDef<any>[]>(
    () => [
      {
        field: "date_create",
        headerName: t("links.columns.date"),
        flex: 1,
        valueFormatter: (value) => {
          return dayjs(value).format("DD.MM.YYYY HH:mm:ss");
        },
      },
      {
        field: "site_name",
        headerName: t("links.columns.resource"),
        flex: 1,
      },
      {
        field: "link",
        headerName: t("links.columns.link"),
        flex: 1,
        sortable: true,
        renderCell: (params: GridRenderCellParams) => (
          <a
            href={params.value}
            target="_blank"
            title={params.value}
            rel="noreferrer"
          >
            {params.value}
          </a>
        ),
      },
      {
        field: "action1",
        headerName: "",
        width: 80,
        sortable: false,
        sx: {
          overflow: "unset",
        },
        type: "actions",
        renderCell: (params: GridRenderCellParams) => (
          <ButtonsBlock>
            <CopyButton link={params.row.link} />

            <IconButton
              aria-label="open link"
              onClick={() => window.open(params.row.link, "_blank")}
              title={t("links.openLink")}
            >
              <OpenInNewIcon />
            </IconButton>
          </ButtonsBlock>
        ),
      },
      {
        field: "name_link",
        headerName: t("links.columns.name"),
        flex: 1,
        sortable: true,
      },
      {
        field: "action2",
        headerName: "",
        width: 25,
        sortable: false,
        type: "actions",
        renderCell: (params: GridRenderCellParams) => (
          <ButtonsBlock>
            <IconButton
              aria-label="delete link"
              onClick={() => handleDeleteLink(params.row)}
              title={t("links.deleteLink")}
            >
              <DeleteIcon />
            </IconButton>
          </ButtonsBlock>
        ),
      },
    ],
    [refreshCount, t],
  );

  return (
    <>
      <Helmet>
        <title>{t("links.metaTitle")}</title>
        <meta name="description" content={t("links.metaDescription")} />
      </Helmet>

      <PageTitle>{t("links.title")}</PageTitle>

      <HeadTabMenu>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCreateLink}
          fullWidth
          startIcon={<AddIcon />}
        >
          {t("links.createNew")}
        </Button>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleUpdate}
          fullWidth
        >
          {t("links.refresh")}
        </Button>
      </HeadTabMenu>

      <DataTable
        columns={columns}
        path={"/links"}
        refresh={refreshCount}
        isShowSearch
      />

      <Popup
        show={showPopup}
        title={t("links.createTitle")}
        width="sm"
        onClose={handleClosePopup}
        ContentComponent={CreateLinkForm}
        contentProps={{
          onClose: handleClosePopup,
          onSuccess: handleSuccessCreate,
        }}
        wrapContent={false}
      />
    </>
  );
};

export default Links;
